import type {
  CalculatedResultRow,
  MatchupRow,
  ParticipantRow,
  ResultValue,
} from "./types";

export interface ResultsCalculation {
  results: CalculatedResultRow[];
}

export function calculateResults(
  rows: ParticipantRow[] = [],
  matchups: MatchupRow[] = [],
  selectedMatchupIndex = 0,
): ResultsCalculation {
  const matchupIndex = resolveMatchupIndex(selectedMatchupIndex, matchups);

  return {
    results: rows
      .map((participant) =>
        calculateParticipantResult(participant, matchups, matchupIndex),
      )
      .sort((a, b) => b.score - a.score),
  };
}

export function calculateScore(
  row: string | ResultValue[] | undefined = [],
  matchups: MatchupRow[] = [],
): number {
  const picks = typeof row === "string" ? row.split("") : row;
  let sum = 0;

  picks.forEach((result, index) => {
    sum += getScore(result, matchups[index]);
  });

  return roundToThreeDecimals(sum);
}

export function getScore(
  result: ResultValue | undefined,
  matchup?: MatchupRow,
  potential = false,
): number {
  if (!matchup || !result) return 0;
  if (matchup.result !== result && !potential) return 0;

  return roundToThreeDecimals(
    getOdds(result, matchup) * (matchup.multiplier || 1),
  );
}

export function getOdds(
  result: ResultValue | undefined,
  matchup?: MatchupRow,
): number {
  if (!matchup) return 0;

  switch (result) {
    case "1":
      return matchup.oddsHome ?? 0;
    case "X":
      return matchup.oddsDraw ?? 0;
    case "2":
      return matchup.oddsAway ?? 0;
    default:
      return 0;
  }
}

export function roundToThreeDecimals(n: number): number {
  return Math.round(1000 * n) / 1000;
}

function calculateParticipantResult(
  { name, row }: ParticipantRow,
  matchups: MatchupRow[],
  matchupIndex: number,
): CalculatedResultRow {
  const selectedMatchup = matchups[matchupIndex];
  const currentPick = pickAt(row, matchupIndex);

  return {
    name,
    score: calculateScore(row, matchups),
    curScore: getScore(currentPick, selectedMatchup),
    curPotentialScore: getScore(currentPick, selectedMatchup, true),
    curOdds: getOdds(currentPick, selectedMatchup),
    curRow: currentPick,
    curResult: selectedMatchup?.result,
    correct: selectedMatchup?.result === currentPick,
    multiplier: selectedMatchup?.multiplier || 1,
  };
}

function pickAt(row: string | undefined, index: number): ResultValue | undefined {
  return row?.[index] as ResultValue | undefined;
}

function resolveMatchupIndex(
  selectedMatchupIndex: number,
  matchups: MatchupRow[],
): number {
  return selectedMatchupIndex < 0 ? matchups.length - 1 : selectedMatchupIndex;
}
